import React, { useEffect, useRef, useState } from 'react';
import { DataGrid, GridCellParams, GridColDef, GridRowParams, GridActionsCellItem, GridFilterModel } from '@mui/x-data-grid';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Typography } from '@mui/material';
import { useFirebaseService } from "../../data/FirebaseService";
import { DocumentData, QuerySnapshot, Timestamp, where } from 'firebase/firestore';
import { format, subDays } from "date-fns"
import { MessageEntity } from './MessageEntity';
import ConfirmationDialog from '../../ui/ConfirmationDialog';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const MessagesPage: React.FC = () => {
    const [messages, setMessages] = useState<MessageEntity[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const hasLoaded = useRef(false);
    const firebaseService = useFirebaseService();
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openDisableDialog, setOpenDisableDialog] = useState(false);
    const [currentRow, setCurrentRow] = useState<MessageEntity | null>(null);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const getMessageStatus = (row: MessageEntity) => {
        const currentDate = new Date();
        return row.isActive ? (row.endDate.toDate() < currentDate ? 'Expired' : 'Active') : 'Disabled';
    }

    // Apply filter based on URL parameter
    useEffect(() => {
        const filterParam = searchParams.get('filter');
        if (filterParam === 'active') {
            setFilterModel({
                items: [
                    {
                        field: 'isActive',
                        operator: 'equals',
                        value: 'Active'
                    }
                ]
            });
        } else if (filterParam === 'recent') {
            const thirtyDaysAgo = subDays(new Date(), 30);
            setFilterModel({
                items: [
                    {
                        field: 'createdDate',
                        operator: 'after',
                        value: thirtyDaysAgo.toISOString()
                    }
                ]
            });
        } else {
            // Clear filters if no valid filter parameter
            setFilterModel({ items: [] });
        }
    }, [searchParams]);

    const getRowActions = (params: GridRowParams): JSX.Element[] => {
        const result = [];
        if (getMessageStatus(params.row as MessageEntity) === 'Active') {
            result.push(<GridActionsCellItem
                key="disable"
                label="Disable"
                onClick={() => handleDisable(params.row)}
                showInMenu
            />)
        }

        result.push(<GridActionsCellItem
            key="delete"
            label="Delete"
            onClick={() => handleDelete(params.row)}
            showInMenu
        />);

        result.push(<GridActionsCellItem
            key="view"
            label="View Results"
            onClick={() => navigate(`/message/${params.row.id}/results`)}
            showInMenu
        />);

        return result;
    };

    const columns: GridColDef[] = [
        { field: 'title', headerName: 'Title', flex: 1, },
        {
            field: 'isActive',
            headerName: 'Status',
            width: 80,
            valueGetter: (params: boolean, row: MessageEntity) => {
                return getMessageStatus(row);
            }
        },
        {
            field: 'createdDate',
            headerName: 'Created Date',
            width: 180,
            type: 'date',
            valueGetter: (params: Timestamp) => {
                // Convert Timestamp to Date for filtering
                if (params) {
                    return params.toDate();
                }
                return null;
            },
            valueFormatter: (params) => {
                // Format the Date for display
                if (params) {
                    return format(params as Date, "MMM dd, yyyy hh:mm a");
                }
                return '';
            },
        },
        {
            field: 'updatedDate',
            headerName: 'Updated Date',
            width: 180,
            type: 'date',
            valueGetter: (params: Timestamp) => {
                if (params) {
                    return params.toDate();
                }
                return null;
            },
            valueFormatter: (params) => {
                if (params) {
                    return format(params as Date, "MMM dd, yyyy hh:mm a");
                }
                return '';
            }
        },
        {
            field: 'endDate',
            headerName: 'End Date',
            width: 180,
            type: 'date',
            valueGetter: (params: Timestamp) => {
                if (params) {
                    return params.toDate();
                }
                return null;
            },
            valueFormatter: (params) => {
                if (params) {
                    return format(params as Date, "MMM dd, yyyy hh:mm a");
                }
                return '';
            }
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 60,
            getActions: getRowActions,
        }
    ];



    const handleCreateMessage = () => {
        navigate('/create-message');
    };

    const handleDisable = (row: MessageEntity) => {
        setCurrentRow(row);
        setOpenDisableDialog(true);
    };

    const handleDelete = (row: MessageEntity) => {
        setCurrentRow(row);
        setOpenDeleteDialog(true);
    };

    const onSnapShot = (snapshot: QuerySnapshot<DocumentData, DocumentData>): void => {
        const updatedDocs: MessageEntity[] = [];
        snapshot.forEach((doc) => {
            const message = doc.data() as MessageEntity;
            message.id = doc.id;
            updatedDocs.push(message);
        });
        setMessages(updatedDocs);

        setIsLoading(false);
        hasLoaded.current = true;
    };

    const handleRowClicked = (row: GridRowParams) => {
        //console.log('Row clicked:', row);
        //navigate(`/edit-message/${row.id}`);
    }

    const handleCellClicked = (params: GridCellParams) => {
        console.log('Row clicked:', params.row.id);
        if (params.field === 'title') {
            navigate(`/edit-message/${params.row.id}`);
        }
    }

    const handleConfirmDisable = async () => {
        if (firebaseService && currentRow) {
            console.log('Disable action confirmed for:', currentRow.id);
            const disableMessage = httpsCallable(firebaseService.functions, "disableMessage");
            disableMessage({ messageId: currentRow.id }).then(() => {
                toast.success('Message disabled successfully');
            }).catch((error) => {
                toast.error('Error disabling message');
            });

            setOpenDisableDialog(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (firebaseService && currentRow) {
            console.log('Delete action confirmed for:', currentRow.id);
            const deleteMessage = httpsCallable(firebaseService.functions, "deleteMessage");
            deleteMessage({ messageId: currentRow.id }).then(() => {
                toast.success('Message deleted successfully');
            }).catch((error) => {
                toast.error('Error deleting message');
            });

            setOpenDeleteDialog(false);
        }
    };

    // Fetch documents once on mount
    useEffect(() => {
        if (firebaseService) {
            // Subscribe to real-time updates
            firebaseService.subscribeToDocuments('messages', onSnapShot, where('isDeleted', '!=', true));
        }
    }, [firebaseService]);

    return (
        <div style={{

            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h5" component="h2" className='page-title'>
                    Messages
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCreateMessage}
                >
                    Create Message
                </Button>
            </div>

            <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, flexGrow: 1, marginBottom: '20px' }}>
                <div style={{ height: 'calc(100vh - 160px)', width: '100%' }}>
                    <DataGrid
                        rows={messages}
                        loading={isLoading}
                        columns={columns}
                        filterModel={filterModel}
                        onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
                        initialState={{
                            sorting: {
                                sortModel: [
                                    { field: 'createdDate', sort: 'desc' },
                                ]
                            }
                        }}
                        onRowClick={handleRowClicked}
                        onCellClick={handleCellClicked}
                    />
                </div>
            </div>

            <ConfirmationDialog
                open={openDisableDialog}
                title='Confirm Disable'
                content='Are you sure you want to disable this message?'
                onConfirm={handleConfirmDisable}
                onCancel={() => setOpenDisableDialog(false)}
            />
            <ConfirmationDialog
                open={openDeleteDialog}
                title='Confirm Delete'
                content='Are you sure you want to delete this message?'
                onConfirm={handleConfirmDelete}
                onCancel={() => setOpenDeleteDialog(false)}
            />
        </div>
    );
};

export default MessagesPage;